import React, { useState } from "react";
import { Image, Modal, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import OSS from "ali-oss";
import { confirmIdentityFile, requestFileUploadToken } from "../api/user";
import "./OssUpload.css";

/**
 * 通用 OSS 文件选择与预览列表。
 * 默认使用通用文件接口，也允许调用方注入业务专用的上传逻辑。
 */
function OssUpload({
  fileList: controlledFileList,
  onChange,
  onRemove,
  uploadFile = uploadToOss,
  accept,
  multiple = false,
  maxCount,
  disabled = false,
  className,
  triggerText = "添加文件",
}) {
  const [internalFileList, setInternalFileList] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const fileList = controlledFileList ?? internalFileList;
  const handleUpload = async ({ file, onSuccess, onError }) => {
    try {
      if (!uploadFile) throw new Error("未配置文件上传方式");
      onSuccess(await uploadFile(file));
    } catch (error) {
      onError(error);
    }
  };

  return (
    <>
      <Upload
        className={["oss-upload", className].filter(Boolean).join(" ")}
        listType="picture-card"
        accept={accept}
        multiple={multiple}
        maxCount={maxCount}
        fileList={fileList}
        customRequest={handleUpload}
        onChange={(info) => {
          if (controlledFileList === undefined) {
            setInternalFileList(info.fileList);
          }
          onChange?.(info);
        }}
        onRemove={onRemove}
        onPreview={(file) => setPreviewFile(file)}
        disabled={disabled}
        showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
      >
        {(maxCount === undefined || fileList.length < maxCount) && (
          <div className="upload-trigger">
            <PlusOutlined />
            <span>{triggerText}</span>
          </div>
        )}
      </Upload>
      <Modal
        open={Boolean(previewFile)}
        title={previewFile?.name}
        footer={null}
        onCancel={() => setPreviewFile(null)}
        width="min(900px, calc(100vw - 32px))"
        destroyOnClose
        styles={{
          body: { padding: 12, maxHeight: "75vh", overflow: "auto" },
        }}
      >
        {previewFile &&
          (previewFile.type?.startsWith("image/") ||
          /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(?:[?#]|$)/i.test(
            previewFile.url || previewFile.name || "",
          ) ? (
            <Image
              src={previewFile.url || previewFile.thumbUrl}
              alt={previewFile.name}
              preview={false}
              className="oss-upload-preview-image"
            />
          ) : (
            <iframe
              src={previewFile.url || previewFile.thumbUrl}
              title={previewFile.name || "文件预览"}
              className="oss-upload-preview-frame"
            />
          ))}
      </Modal>
    </>
  );
}

async function uploadToOss(file) {
  if (!file.type.startsWith("image/")) throw new Error("请选择图片");
  if (file.size > 20 * 1024 * 1024) throw new Error("图片不能超过20MB");

  const { data } = await requestFileUploadToken({
    originalName: file.name,
    contentType: file.type,
    size: file.size,
  });
  const client = new OSS({
    region: data.region,
    bucket: data.bucket,
    endpoint: data.endpoint,
    secure: true,
    accessKeyId: data.credentials.accessKeyId,
    accessKeySecret: data.credentials.accessKeySecret,
    stsToken: data.credentials.securityToken,
  });
  await client.put(data.objectKey, file);
  const { data: confirmData } = await confirmIdentityFile({
    objectKey: data.objectKey,
    originalName: file.name,
    contentType: file.type,
    size: file.size,
  });
  return { file: confirmData.file };
}

export default OssUpload;
